import { faArrowsRotate } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQueryClient } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';
import { getAppScrollTop } from '../lib/appScroll';
import { triggerLightImpact } from '../lib/haptics';

/**
 * Pull-to-refresh for the storefront's window-scroll pages (PD-14694).
 *
 * Mounted once in PortalLayout, so every data surface (dashboard,
 * invoices, statements, transactions, orders, …) gets the same gesture:
 * pull down from the very top of the page → a branded indicator tracks
 * the drag → past the threshold, release refetches every ACTIVE
 * react-query query on the current screen (exactly the data the user is
 * looking at; each surface's own loading/skeleton affordances take over
 * from there).
 *
 * Constraints that shaped the implementation:
 *  - The scroll offset lives on the window on desktop/Android but on
 *    `document.body` in the iOS WKWebView (see `lib/appScroll.ts`) — so
 *    the gesture arms only while `getAppScrollTop() === 0` and cancels
 *    the moment the page actually scrolls. Reading only `window.scrollY`
 *    here armed the gesture from ANY scroll position in the native app,
 *    hijacking quick scroll-up flicks into refreshes.
 *  - Touch-only: the listeners are never attached on fine-pointer
 *    (desktop) devices, leaving desktop web untouched.
 *  - Overlays: a pull that starts inside a dialog/drawer (login modal,
 *    menu drawer, pay sidebar) is ignored, as is everything while body
 *    scroll is locked by an overlay.
 *  - `preventDefault` on the tracked pull suppresses the WebView's
 *    rubber-band so the indicator — not the page — follows the finger
 *    (hence the non-passive touchmove listener).
 */

/** Resisted drag distance (px) that arms the refresh on release. */
const REFRESH_THRESHOLD_PX = 64;
/** Cap on the indicator travel so long drags don't fly off the page. */
const MAX_PULL_PX = 96;
/** Drag resistance — screen px are multiplied down to indicator px. */
const PULL_RESISTANCE = 0.45;

export function PullToRefresh(): ReactElement | null {
	const queryClient = useQueryClient();
	const [pull, setPull] = useState(0);
	const [isDragging, setIsDragging] = useState(false);
	const [isRefreshing, setIsRefreshing] = useState(false);
	const isRefreshingRef = useRef(false);

	useEffect(() => {
		// Touch-only. `pointer: coarse` matches phones/tablets (and the
		// Capacitor WebView); desktop trackpads/mice never attach.
		if (!window.matchMedia('(pointer: coarse)').matches) {
			return undefined;
		}

		let startY = -1;
		let tracking = false;
		let armed = false;

		const resetPull = (): void => {
			tracking = false;
			armed = false;
			startY = -1;
			setIsDragging(false);
			setPull(0);
		};

		const runRefresh = (): void => {
			isRefreshingRef.current = true;
			setIsRefreshing(true);
			setIsDragging(false);
			setPull(REFRESH_THRESHOLD_PX);
			triggerLightImpact();
			void queryClient
				.refetchQueries({ type: 'active' })
				.catch(() => {
					// Individual query errors surface through their own UIs.
				})
				.finally(() => {
					isRefreshingRef.current = false;
					setIsRefreshing(false);
					setPull(0);
				});
		};

		const onTouchStart = (e: TouchEvent): void => {
			if (isRefreshingRef.current || getAppScrollTop() > 0) {
				return;
			}
			// Ignore pulls that begin inside an overlay (modal, drawer,
			// sidebar) or while an overlay has locked body scroll — the
			// gesture belongs to the page underneath, not the overlay.
			const target = e.target as Element | null;
			if (
				target?.closest('[role="dialog"], [aria-modal="true"]') != null ||
				document.body.style.overflow === 'hidden'
			) {
				return;
			}
			startY = e.touches[0].clientY;
			tracking = true;
			armed = false;
		};

		const onTouchMove = (e: TouchEvent): void => {
			if (!tracking) {
				return;
			}
			// The page took over (scrolled away from top) — stand down.
			if (getAppScrollTop() > 0) {
				resetPull();
				return;
			}
			const dy = e.touches[0].clientY - startY;
			if (dy <= 0) {
				setIsDragging(false);
				setPull(0);
				armed = false;
				return;
			}
			const distance = Math.min(dy * PULL_RESISTANCE, MAX_PULL_PX);
			// Once the pull is clearly intentional, claim the gesture from
			// the WebView's rubber-band/native scroll.
			if (distance > 6 && e.cancelable) {
				e.preventDefault();
			}
			armed = distance >= REFRESH_THRESHOLD_PX;
			setIsDragging(true);
			setPull(distance);
		};

		const onTouchEnd = (): void => {
			if (!tracking) {
				return;
			}
			const shouldRefresh = armed;
			tracking = false;
			armed = false;
			startY = -1;
			setIsDragging(false);
			if (shouldRefresh) {
				runRefresh();
			} else {
				setPull(0);
			}
		};

		document.addEventListener('touchstart', onTouchStart, { passive: true });
		// Non-passive on purpose — see preventDefault above.
		document.addEventListener('touchmove', onTouchMove, { passive: false });
		document.addEventListener('touchend', onTouchEnd);
		document.addEventListener('touchcancel', onTouchEnd);
		return (): void => {
			document.removeEventListener('touchstart', onTouchStart);
			document.removeEventListener('touchmove', onTouchMove);
			document.removeEventListener('touchend', onTouchEnd);
			document.removeEventListener('touchcancel', onTouchEnd);
		};
	}, [queryClient]);

	if (pull <= 0 && !isRefreshing) {
		return null;
	}

	const progress = Math.min(pull / REFRESH_THRESHOLD_PX, 1);

	return (
		<div
			className="fixed inset-x-0 z-40 flex justify-center pointer-events-none"
			style={{ top: 'env(safe-area-inset-top, 0px)' }}
			aria-hidden="true"
		>
			<div
				className="w-9 h-9 rounded-full bg-brand-surface border border-brand-border shadow-md flex items-center justify-center"
				style={{
					transform: `translateY(${pull - 44}px) rotate(${pull * 3}deg)`,
					opacity: isRefreshing ? 1 : progress,
					// Track the finger 1:1 while dragging; ease home on release.
					transition: isDragging
						? 'none'
						: 'transform 200ms ease, opacity 200ms ease',
				}}
			>
				<FontAwesomeIcon
					icon={faArrowsRotate}
					className={`text-portal-accent ${
						isRefreshing ? 'motion-safe:animate-spin' : ''
					}`}
				/>
			</div>
		</div>
	);
}
